/**
 * The scope keys this app checks, matching `iam.Scopes.FullPath` and the backend's `YtScopes`.
 *
 * One key, because one screen asks one question. The catalog is the engine's and is far wider than
 * this; mirroring the rest of it here would put names in front of a reader with nothing behind them,
 * and each would have to be kept honest against a catalog no screen was reading. A screen that comes
 * to ask a new question adds its key then.
 *
 * A capability is the only thing Access Management authorizes on. A role is a named bundle of these
 * and a household relationship is a domain fact, so neither can answer whether someone may act.
 */
export enum AppScope {
  InviteMembers = 'Members.Invite'
}

/**
 * Cascading scope match mirroring the backend `ScopeMatch.Allows`: `*` answers anything; `X.*`
 * answers `X` and any deeper `X.<child>`; a leaf answers only itself. Case-insensitive.
 *
 * A leaf never answers its own wildcard, which is what keeps a family scope stronger than the one
 * delegable action beneath it.
 *
 * The wildcard belongs to the granted side, which is the engine's effective-scope set rather than
 * anything this app composes: the seeded Admin bundle carries `Orders.*` and `Plan.*`. So the
 * cascade stays whichever single leaf the `requested` side happens to name today.
 */
export function scopeAllows(granted: string, requested: string): boolean {
  if (!granted || !requested) return false;
  if (granted === '*') return true;
  if (granted.toLowerCase() === requested.toLowerCase()) return true;
  if (!granted.endsWith('.*')) return false;

  const prefix = granted.slice(0, -2).toLowerCase();
  const requestedLower = requested.toLowerCase();
  if (requestedLower === prefix) return true;
  return requestedLower.length > prefix.length && requestedLower.startsWith(prefix + '.');
}

export function scopesAllow(granted: readonly string[] | undefined, requested: string): boolean {
  return (granted ?? []).some((held) => scopeAllows(held, requested));
}

/**
 * Whether a scope key confers authority over paths beyond itself, mirroring the backend
 * `ScopeMatch.IsWildcard`.
 *
 * The distinction the effective-scope fold turns on: a deny subtracts a wildcard's LEAVES, and what
 * is left of the wildcard afterwards is what decides whether a capability is gone or merely narrowed.
 */
export function isWildcardScope(scope: string): boolean {
  return !!scope && (scope === '*' || scope.endsWith('.*'));
}
